import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import ContentPageLayout from "../components/ContentPageLayout";
import JsonLd, { breadcrumbList } from "../components/JsonLd";
import { GUIDES_BY_SLUG } from "../content/guides";
import { trackEvent } from "../utils/analytics";

const GuidePage = () => {
  const { slug } = useParams();
  const guide = GUIDES_BY_SLUG[slug];

  useEffect(() => {
    if (guide) trackEvent("guide_viewed", { slug });
  }, [slug, guide]);

  if (!guide) {
    return (
      <ContentPageLayout>
        <main style={styles.main}>
          <h1 style={styles.title}>Guide not found</h1>
          <p style={styles.lede}>
            That guide doesn't exist. Browse all of our{" "}
            <Link to="/guides" style={styles.inlineLink}>comic-making guides</Link>.
          </p>
        </main>
      </ContentPageLayout>
    );
  }

  const related = (guide.related || [])
    .map((s) => GUIDES_BY_SLUG[s])
    .filter(Boolean);

  const isStoryboardGuide = guide.cluster === "storyboards";

  return (
    <ContentPageLayout>
      <JsonLd data={breadcrumbList([
        { name: "Guides", path: "/guides" },
        { name: guide.title, path: `/guides/${guide.slug}` },
      ])} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: guide.title,
        description: guide.description,
        mainEntityOfPage: `https://striply.xyz/guides/${guide.slug}`,
        publisher: { "@type": "Organization", name: "Striply" },
      }} />
      <main style={styles.main}>
        <nav style={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/guides" style={styles.inlineLink}>Guides</Link>
          <span style={styles.crumbSep}>/</span>
          <span>{guide.title}</span>
        </nav>

        <h1 style={styles.title}>{guide.title}</h1>
        <p style={styles.meta}>{guide.readingTime}</p>

        <article className="guide-content">{guide.body}</article>

        {related.length > 0 && (
          <aside style={styles.related}>
            <h2 style={styles.relatedTitle}>Keep reading</h2>
            <ul style={styles.relatedList}>
              {related.map((r) => (
                <li key={r.slug} style={styles.relatedItem}>
                  <Link to={`/guides/${r.slug}`} style={styles.relatedLink}>
                    {r.title}
                  </Link>
                  <span style={styles.relatedSummary}> — {r.summary}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}

        <div style={styles.ctaSection}>
          {isStoryboardGuide ? (
            <Link to="/storyboard" style={styles.ctaButton}>Make a storyboard</Link>
          ) : (
            <Link to="/editor" style={styles.ctaButton}>Open the Editor</Link>
          )}
        </div>
      </main>
    </ContentPageLayout>
  );
};

const styles = {
  main: {
    flex: 1,
    width: "100%",
    maxWidth: 720,
    margin: "0 auto",
    padding: "40px 24px 48px",
    boxSizing: "border-box",
  },
  breadcrumb: {
    fontSize: 13,
    color: "#999",
    marginBottom: 20,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  crumbSep: { margin: "0 8px" },
  inlineLink: { color: "#00a4e4", textDecoration: "none" },
  title: {
    fontSize: 38,
    fontWeight: 700,
    lineHeight: 1.2,
    margin: "0 0 8px",
    color: "#1a1a1a",
  },
  meta: {
    fontSize: 13,
    color: "#999",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 32,
  },
  lede: { fontSize: 17, color: "#555" },
  related: {
    marginTop: 48,
    borderTop: "2px solid #1a1a1a",
    paddingTop: 20,
  },
  relatedTitle: {
    fontSize: 14,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#999",
    margin: "0 0 12px",
  },
  relatedList: { listStyle: "none", margin: 0, padding: 0 },
  relatedItem: { marginBottom: 10, fontSize: 15, lineHeight: 1.5 },
  relatedLink: { color: "#00a4e4", textDecoration: "none", fontWeight: 700 },
  relatedSummary: { color: "#777", fontSize: 14 },
  ctaSection: { textAlign: "center", padding: "40px 0 8px" },
  ctaButton: {
    display: "inline-block",
    padding: "14px 32px",
    background: "#1a1a1a",
    color: "#f5f0e8",
    fontSize: 16,
    fontWeight: 700,
    textDecoration: "none",
    textTransform: "uppercase",
    letterSpacing: 1,
    border: "2px solid #1a1a1a",
  },
};

export default GuidePage;
